import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Info } from "lucide-react";
import api from "@/lib/api";
import { docsAPI, fmtINR } from "@/lib/documents";

/** Mirrors backend `room_rate_breakdown` (engines/documents/tax_config.py). */
export function rateBreakdown(cfg, entered, inclusive, nights) {
  const amt = Number(entered) || 0;
  const slabs = [...(cfg?.room_slabs || [])].sort((a, b) => (a.upto == null) - (b.upto == null) || (a.upto || 0) - (b.upto || 0));
  const pick = (base) => (slabs.find((s) => s.upto == null || base <= Number(s.upto))?.rate ?? slabs.at(-1)?.rate ?? 0);
  let base = amt, rate = 0;
  if (inclusive) {
    for (const s of slabs) { const c = amt / (1 + Number(s.rate) / 100); if (s.upto == null || c <= Number(s.upto) + 1e-9) { base = c; rate = Number(s.rate); break; } }
  } else { rate = Number(pick(amt)); }
  base = Math.round(base * 100) / 100;
  const half = Math.round(base * rate / 2) / 100;
  const totalNight = Math.round((base + half * 2) * 100) / 100;
  const n = Math.max(Number(nights) || 1, 1);
  return { base, rate, cgst: half, sgst: half, tax: Math.round(half * 200) / 100, totalNight, nights: n, grand: Math.round(totalNight * n * 100) / 100 };
}

export function nightsBetween(a, b) {
  if (!a || !b) return 0;
  const d = (new Date(String(b).slice(0, 10)) - new Date(String(a).slice(0, 10))) / 86400000;
  return d > 0 ? Math.round(d) : 0;
}

const TONES = {
  dark: {
    label: "text-xs uppercase tracking-wider text-hocp-textMuted", trigger: "bg-hocp-bgCard border-hocp-border mt-1.5",
    input: "bg-hocp-bgCard border-hocp-border pl-7 mono", badge: "bg-hocp-bgSubtle text-hocp-textPrimary border-hocp-border",
    panel: "border border-hocp-border bg-hocp-softIce", muted: "text-hocp-textMuted", divider: "border-hocp-border",
    warn: "text-[#78530E] bg-[#FEF3C7] border-[#F59E0B]/50", strong: "text-foreground", row: "text-muted-foreground",
    radio: "accent-[#37F4B5]", radioBox: "border-hocp-border", radioOn: "border-hocp-mint bg-hocp-mintSoft", info: "text-hocp-deepTeal bg-hocp-softIce border-hocp-border",
  },
  light: {
    label: "text-[12px] uppercase tracking-wider text-slate-500 font-semibold", trigger: "border-2 border-slate-300 h-12 mt-1 bg-white text-[14px]",
    input: "border-2 border-slate-300 h-12 pl-7 mono bg-white text-[14px]", badge: "bg-slate-100 text-slate-700 border-slate-200",
    panel: "border border-hocp-border bg-hocp-softIce", muted: "text-slate-500", divider: "border-slate-300",
    warn: "text-amber-900 bg-amber-50 border-amber-300", strong: "text-slate-900", row: "text-slate-600",
    radio: "accent-[#37F4B5]", radioBox: "border-hocp-border bg-white", radioOn: "border-hocp-mint bg-hocp-mintSoft", info: "text-sky-900 bg-sky-50 border-sky-200",
  },
};

/**
 * Shared Per Night Rate field — rate plan autofill, Tax Exclusive / Tax Inclusive selector, live breakdown.
 * Used by New Reservation, Walk-In and New Stay. Any future rate input must reuse this component.
 * value = { entered_rate, tax_inclusive_input, rate_plan_id }
 * onBreakdown(bd | null) fires whenever the computed breakdown changes (for footer totals).
 */
export function RateInputWithTax({ value, onChange, nights, roomTypeId, onConfigured, onBreakdown, tone = "dark", testidPrefix = "rate" }) {
  const t = TONES[tone] || TONES.dark;
  const [cfg, setCfg] = useState(null);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    docsAPI.taxConfig().then((c) => { setCfg(c); onConfigured?.(!!c.configured); }).catch(() => { setCfg({ room_slabs: [], configured: false }); onConfigured?.(false); });
    api.get("/master/rate-plans/?page_size=100").then((r) => setPlans((r.data.items || r.data || []).filter((p) => p.is_active !== false))).catch(() => setPlans([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const inclusive = !!value.tax_inclusive_input;
  const hasRate = Number(value.entered_rate) > 0;
  const bd = useMemo(() => rateBreakdown(cfg, value.entered_rate, inclusive, nights), [cfg, value.entered_rate, inclusive, nights]);
  useEffect(() => { onBreakdown?.(hasRate && cfg ? bd : null); }, [bd, cfg, hasRate]); // eslint-disable-line react-hooks/exhaustive-deps

  const plan = plans.find((p) => p.id === value.rate_plan_id);
  const visiblePlans = roomTypeId ? plans.filter((p) => !p.room_type_id || p.room_type_id === roomTypeId) : plans;
  const notConfigured = cfg && !cfg.configured;

  const pickPlan = (id) => {
    const p = plans.find((x) => x.id === id);
    onChange({ rate_plan_id: id === "none" ? "" : id, tax_inclusive_input: false, entered_rate: p && Number(p.base_rate) > 0 ? Number(p.base_rate) : value.entered_rate });
  };

  const pct = bd.rate / 2;
  const rows = inclusive
    ? [["Total / Night (entered)", bd.totalNight, "total-night", true], ["Base Rate (extracted)", bd.base, "base"], [`CGST @ ${pct}% (of base)`, bd.cgst, "cgst"], [`SGST @ ${pct}% (of base)`, bd.sgst, "sgst"], "hr", [`Total for ${bd.nights} Night${bd.nights === 1 ? "" : "s"}`, bd.grand, "grand", true]]
    : [["Base Rate / Night", bd.base, "base"], [`CGST @ ${pct}%`, bd.cgst, "cgst"], [`SGST @ ${pct}%`, bd.sgst, "sgst"], "hr", ["Total / Night", bd.totalNight, "total-night", true], [`Total for ${bd.nights} Night${bd.nights === 1 ? "" : "s"}`, bd.grand, "grand", true]];

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-input`}>
      {notConfigured && (
        <div className={`flex items-start gap-2 text-[12px] border rounded-md px-3 py-2 ${t.warn}`} data-testid={`${testidPrefix}-tax-warning`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>Tax rates not configured for this hotel. Set them in <Link to="/master/billing-settings" className="underline font-medium">Hotel Settings › Billing &amp; Tax</Link> before creating bookings.</div>
        </div>)}
      <div>
        <Label className={t.label}>Rate plan</Label>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={value.rate_plan_id || "none"} onValueChange={pickPlan}>
            <SelectTrigger className={`${t.trigger} flex-1 min-w-[200px]`} data-testid={`${testidPrefix}-plan`}><SelectValue placeholder="No rate plan" /></SelectTrigger>
            <SelectContent><SelectItem value="none">No rate plan (manual rate)</SelectItem>{visiblePlans.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}{Number(p.base_rate) > 0 ? ` · ${fmtINR(p.base_rate)}` : ""}</SelectItem>)}</SelectContent>
          </Select>
          {plan && <span className={`inline-block mt-1 text-[11px] mono px-2 py-1 rounded border ${t.badge}`} data-testid={`${testidPrefix}-plan-badge`}>Base: {fmtINR(plan.base_rate)}/night{plan.meal_plan ? ` · ${plan.meal_plan}` : ""}</span>}
        </div>
      </div>
      <div>
        <Label className={t.label}>Per night rate</Label>
        <div className="grid grid-cols-2 gap-2 mt-1.5" role="radiogroup">
          {[[false, "Tax Exclusive", "Tax added on top"], [true, "Tax Inclusive", "Tax already in price"]].map(([v, l, sub]) => (
            <label key={l} className={`flex items-start gap-2 cursor-pointer text-[12.5px] rounded-md border px-3 py-2 ${inclusive === v ? t.radioOn : t.radioBox}`}>
              <input type="radio" name={`${testidPrefix}-mode`} checked={inclusive === v} onChange={() => onChange({ ...value, tax_inclusive_input: v })} className={`mt-1 ${t.radio}`} data-testid={`${testidPrefix}-${v ? "inclusive" : "exclusive"}`} />
              <span><span className={`font-medium ${t.strong}`}>{l}</span><br /><span className={`text-[11px] ${t.muted}`}>{sub}</span></span>
            </label>))}
        </div>
        <Label className={`${t.label} block mt-3`}>{inclusive ? "Enter all-inclusive price per night" : "Enter base rate per night"}</Label>
        <div className="relative mt-1.5">
          <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-[13px] ${t.muted}`}>₹</span>
          <Input type="number" step="0.01" min="0" value={value.entered_rate ?? ""} onChange={(e) => onChange({ ...value, entered_rate: e.target.value === "" ? "" : +e.target.value })} className={t.input} data-testid={`${testidPrefix}-amount`} />
        </div>
        <div className={`text-[11px] mt-1 ${t.muted}`} data-testid={`${testidPrefix}-hint`}>{inclusive ? "Enter the all-inclusive price guest will pay per night" : "Enter base rate — tax will be added on top"}</div>
      </div>
      {hasRate && cfg && (
        <div className={`rounded-md p-3 text-[12px] space-y-1 ${t.panel}`} data-testid={`${testidPrefix}-breakdown`}>
          <div className={`text-[10px] mono uppercase tracking-wider mb-1 ${t.muted}`}>Rate breakdown · GST {bd.rate}%</div>
          {rows.map((r, i) => r === "hr" ? <div key={i} className={`border-t my-1 ${t.divider}`} /> : (
            <div key={r[2]} className={`flex justify-between ${r[3] ? `font-semibold ${t.strong}` : t.row}`}><span>{r[0]}</span><span className="mono" data-testid={`${testidPrefix}-bd-${r[2]}`}>{fmtINR(r[1])}</span></div>))}
          <div className={`text-[11px] pt-1 ${t.muted}`} data-testid={`${testidPrefix}-bd-note`}>
            {inclusive ? "Same total — system extracts base for tax records." : `Guest pays ${fmtINR(bd.totalNight)}/night. Base ${fmtINR(bd.base)} + tax ${fmtINR(bd.tax)}.`}
          </div>
          {inclusive && (
            <div className={`flex items-start gap-1.5 text-[11px] border rounded px-2 py-1.5 mt-1 ${t.info}`} data-testid={`${testidPrefix}-receipt-note`}>
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Receipt will show: Base {fmtINR(bd.base)} + CGST {fmtINR(bd.cgst)} + SGST {fmtINR(bd.sgst)} = <b>{fmtINR(bd.totalNight)}</b></span>
            </div>)}
        </div>)}
    </div>
  );
}

export const RateInput = RateInputWithTax;
